import { unref, computed } from 'vue'
import { ACTIVITY_CODE, DATE_FORMAT_SLASH } from '@/constants'
import i18n from '@/i18n'
import moment from 'moment'

/**
 * 停止時間の表示を切り替える閾値（分）
 */
const THRESHOLD_MINUTES = {
  LESS_THAN_ONE: 1,
  LESS_THAN_FIVE: 5,
  LESS_THAN_THIRTY: 30,
}

/**
 * 時間表示と日数表示を切り替える閾値（時間）
 */
const HOURS_IN_DAY = 24

/**
 * 24時間未満の場合の経過時間と表示テキストの対応表
 * @type {Array<{limit: number, i18nKey: string}>}
 */
const MINUTE_THRESHOLDS = [
  { limit: THRESHOLD_MINUTES.LESS_THAN_ONE, i18nKey: 'lessThanOneMinute' },
  { limit: THRESHOLD_MINUTES.LESS_THAN_FIVE, i18nKey: 'lessThanFiveMinutes' },
  {
    limit: THRESHOLD_MINUTES.LESS_THAN_THIRTY,
    i18nKey: 'lessThanThirtyMinutes',
  },
]

/**
 * ユーザーリスト表示項目(アクティビティ)の利用
 * @param {MaybeRef.<string>} value アクティビティ値
 * @param {MaybeRef.<string>} changeAt 変更日時
 * @param {MaybeRef.<string>} baseDate 基準日時
 */
export function useUserStatusAdditionalItemActivity({
  value,
  changeAt,
  baseDate,
}) {
  /**
   * 停止中の場合はtrue
   * @type {ComputedRef.<boolean>}
   */
  const isStill = computed(() => unref(value) === ACTIVITY_CODE.STILL)

  /**
   * フォーマット済み変更日
   * @type {ComputedRef.<string>}
   */
  const formattedChangeAt = computed(() => {
    const cAt = unref(changeAt)
    return cAt ? moment(cAt).format(DATE_FORMAT_SLASH) : ''
  })

  /**
   * stillの経過日数
   * @type {ComputedRef.<number>}
   */
  const stillDays = computed(() => {
    const cAt = unref(changeAt)
    const bDate = unref(baseDate)
    if (!cAt) return 0
    const base = moment(bDate || new Date())
    const changed = moment(cAt)
    return base.diff(changed, 'days')
  })

  /**
   * アクティビティ値表示テキスト
   * @type {ComputedRef.<string>}
   */
  const valueText = computed(() => {
    const v = unref(value)
    if (v == null) return '-'
    return isStill.value
      ? i18n.t('useUserStatusAdditionalItemActivity.still')
      : i18n.t('useUserStatusAdditionalItemActivity.working')
  })

  /**
   * アクティビティ表示テキスト
   * 稼働中の場合は、稼働状態のテキストをそのまま表示する。
   * 停止中の場合は、停止してからの経過時間に応じて表示テキストを切り替える。
   * - 24時間以上経過: 「N日間」と表示する。 (例: `停止中 1日間`)
   *   - 48時間以上経過している場合は、変更日も合わせて表示する (例: `停止中 2日間 (2025/10/21)`)
   * - 24時間未満: 経過時間に応じて「N時間未満」「30分未満」「5分未満」「1分未満」のように表示を切り替える
   * @type {ComputedRef.<string>}
   */

  const text = computed(() => {
    if (!isStill.value) return valueText.value

    const changeAtMoment = moment(changeAt.value)
    const baseDateMoment = moment(baseDate.value)
    const durationMs = baseDateMoment.diff(changeAtMoment)
    const duration = moment.duration(durationMs)
    const totalHours = Math.floor(duration.asHours())

    if (totalHours >= HOURS_IN_DAY) {
      // 24時間以上経過している場合
      let text = `${valueText.value} ${i18n.tc('useUserStatusAdditionalItemActivity.days', stillDays.value)}`
      if (stillDays.value > 1) {
        text += ` (${formattedChangeAt.value})`
      }
      return text
    } else {
      // 24時間未満の場合
      const totalMinutes = duration.asMinutes() // asMinutes() は合計の分数を返す
      for (const { limit, i18nKey } of MINUTE_THRESHOLDS) {
        if (totalMinutes < limit) {
          return `${valueText.value} ${i18n.t(`useUserStatusAdditionalItemActivity.${i18nKey}`)}`
        }
      }
      return `${valueText.value} ${i18n.tc('useUserStatusAdditionalItemActivity.hours', totalHours + 1)}`
    }
  })

  return { isStill, stillDays, valueText, text }
}
